import { Box, Typography, Button, Container } from '@mui/material';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import { tokens } from '../theme';

const CLOUDFRONT_URL = process.env.REACT_APP_CLOUDFRONT_URL;

/**
 * Hero — direction A, "full bleed".
 *
 * The photograph stops being a card and becomes the page: no frame, no gutters,
 * no second image. One picture, one sentence, one action. The boldness is in
 * what was removed, not in anything added.
 */
const HeroSection = () => {
  const mainImage = `${CLOUDFRONT_URL}/public/cover1.webp`;
  const { t } = useTranslation();

  const [entered, setEntered] = useState(false);
  useEffect(() => {
    const frame = requestAnimationFrame(() => setEntered(true));
    return () => cancelAnimationFrame(frame);
  }, []);

  const handleButtonClick = () => {
    document
      .getElementById('menu-preview-section')
      ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const enter = (delay: number) => ({
    opacity: entered ? 1 : 0,
    transform: entered ? 'none' : 'translateY(18px)',
    filter: entered ? 'blur(0px)' : 'blur(8px)',
    transition: [
      `opacity 700ms ${tokens.ease.out} ${delay}ms`,
      `transform 700ms ${tokens.ease.out} ${delay}ms`,
      `filter 700ms ${tokens.ease.out} ${delay}ms`,
    ].join(', '),
    '@media (prefers-reduced-motion: reduce)': {
      opacity: 1,
      transform: 'none',
      filter: 'none',
    },
  });

  return (
    <Box
      id="hero-section"
      sx={{
        position: 'relative',
        minHeight: { xs: '82vh', md: '88vh' },
        display: 'flex',
        alignItems: 'flex-end',
        overflow: 'hidden',
        // Stands in behind the photograph while it decodes, so the entrance
        // never flashes the page's cream through a full-bleed panel.
        backgroundColor: tokens.color.ink,
      }}
    >
      <Box
        component="img"
        src={mainImage}
        alt={(t as any)('alt.heroCover')}
        fetchPriority="high"
        decoding="async"
        sx={{
          position: 'absolute',
          inset: 0,
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          objectPosition: 'center',
          transform: entered ? 'scale(1)' : 'scale(1.08)',
          transition: `transform 1600ms ${tokens.ease.out}`,
          '@media (prefers-reduced-motion: reduce)': { transform: 'none' },
        }}
      />

      {/*
        Full bleed means the headline can land on any part of the photograph,
        including its brightest. This scrim holds ~0.7 opacity through the whole
        text band, which keeps display-size white type above the 3:1 floor even
        over a near-white tablecloth, then clears entirely by 85% so the food
        above it stays bright.
      */}
      <Box
        aria-hidden="true"
        sx={{
          position: 'absolute',
          inset: 0,
          background: [
            'linear-gradient(to top,',
            'rgba(28,18,15,0.88) 0%,',
            'rgba(28,18,15,0.72) 28%,',
            'rgba(28,18,15,0.42) 48%,',
            'rgba(28,18,15,0.12) 68%,',
            'rgba(28,18,15,0) 85%)',
          ].join(' '),
        }}
      />

      <Container maxWidth="xl" sx={{ position: 'relative', pb: { xs: 6, md: 10 } }}>
        <Typography
          variant="h1"
          component="h1"
          sx={{
            color: tokens.color.white,
            fontWeight: 800,
            // The h1 token amplified for this one surface, held at the 6rem
            // display ceiling rather than invented past it.
            fontSize: 'clamp(2.75rem, 1.2rem + 6.4vw, 6rem)',
            lineHeight: 0.98,
            letterSpacing: '-0.045em',
            maxWidth: '15ch',
            textWrap: 'balance',
            textShadow: '0 2px 40px rgba(28,18,15,0.45)',
            '& strong': { fontWeight: 800 },
            ...enter(80),
          }}
          dangerouslySetInnerHTML={{ __html: (t as any)('hero.title') }}
        />

        <Box sx={{ mt: { xs: 3.5, md: 5 }, ...enter(220) }}>
          <Button
            variant="contained"
            size="large"
            endIcon={<ArrowForwardIosIcon sx={{ fontSize: '0.75rem !important' }} />}
            onClick={handleButtonClick}
            sx={{ minWidth: { xs: '100%', sm: 280 } }}
          >
            {(t as any)('hero.buttonText')}
          </Button>
        </Box>
      </Container>
    </Box>
  );
}

export default HeroSection;
